import { useEffect, useState } from "react";

import ConfirmPanel from "../components/ConfirmPanel";
import ScopeGate from "../components/ScopeGate";
import { Field, SaveBar, SectionCard, Select } from "../components/form";
import { useEditableSection } from "../hooks/useEditableSection";
import {
  deleteRealmRenderProfile,
  fetchRealmRenderProfile,
  fetchRealmRenderProfileImage,
  fetchRealmRenderProfiles,
  setRealmRenderProfile,
} from "../lib/api";
import { describeApiError } from "../lib/apiError";
import { canAdminRealm, canUsePlatformProfiles } from "../lib/scopes";
import { useGrants } from "../lib/session";
import type { RealmRenderProfile, RealmRenderProfileSet, RenderProfileBinding } from "../lib/types";
import { useRealmContext } from "./RealmLayout";

/** Token keys a realm may override, in the order the server accepts them. */
const OVERRIDABLE_TOKENS = ["background", "surface", "text", "muted", "border", "font_sans", "font_mono"];

function isColour(value: unknown): value is string {
  return typeof value === "string" && /^#[0-9A-Fa-f]{6}$/.test(value);
}

function Swatch({ label, value, overridden }: { label: string; value: string; overridden: boolean }) {
  return (
    <div className="flex items-center gap-2">
      <span
        className="inline-block w-6 h-6 rounded border border-kw-border-subtle shrink-0"
        style={{ backgroundColor: value }}
      />
      <span className="text-xs">
        <span className="text-kw-text-muted">{label}</span>{" "}
        <span className="font-mono text-kw-text-faint">{value}</span>
        {overridden && <span className="text-amber-400"> · realm override</span>}
      </span>
    </div>
  );
}

/**
 * The tokens as they would actually apply: the profile's own values with the
 * realm's `style_token_overrides` laid on top. Showing only the declared values
 * would make an override invisible in exactly the view meant to explain the look.
 */
function effectiveTokens(
  profile: RealmRenderProfile,
  overrides: Record<string, string>,
): { key: string; value: unknown; overridden: boolean }[] {
  const merged = new Map<string, { value: unknown; overridden: boolean }>();
  for (const [key, value] of Object.entries(profile.tokens)) {
    merged.set(key, { value, overridden: false });
  }
  for (const [key, value] of Object.entries(overrides)) {
    if (OVERRIDABLE_TOKENS.includes(key)) merged.set(key, { value, overridden: true });
  }
  return [...merged.entries()].map(([key, v]) => ({ key, ...v }));
}

function ReferenceImages({ realmId, profile }: { realmId: string; profile: RealmRenderProfile }) {
  const [urls, setUrls] = useState<string[]>([]);
  const [error, setError] = useState("");

  useEffect(() => {
    let live = true;
    const created: string[] = [];
    setUrls([]);
    setError("");
    void (async () => {
      try {
        for (let i = 0; i < profile.reference_images.length; i += 1) {
          const url = await fetchRealmRenderProfileImage(realmId, profile.id, i);
          created.push(url);
          if (!live) break;
          setUrls([...created]);
        }
      } catch (e: unknown) {
        if (live) setError(describeApiError(e).message);
      }
    })();
    return () => {
      live = false;
      // Object URLs are process-lifetime unless revoked; a profile list the
      // operator clicks through would otherwise leak one blob per thumbnail.
      created.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [realmId, profile.id, profile.reference_images.length]);

  if (profile.reference_images.length === 0) {
    return (
      <div className="text-xs text-kw-text-faint">
        No anchor images — illustrations are styled by the visual guidance text alone.
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {error && <div className="text-xs text-amber-400">{error}</div>}
      <div className="flex flex-wrap gap-2">
        {urls.map((url, i) => (
          <img
            key={url}
            src={url}
            alt={profile.reference_images[i]}
            title={profile.reference_images[i]}
            className="h-24 w-auto rounded border border-kw-border-subtle bg-kw-bg-raised"
          />
        ))}
      </div>
    </div>
  );
}

function ProfileDetail({
  realmId,
  profile,
  overrides,
  isBound,
  packDefaultOf,
}: {
  realmId: string;
  profile: RealmRenderProfile;
  overrides: Record<string, string>;
  isBound: boolean;
  packDefaultOf: string[];
}) {
  const tokens = effectiveTokens(profile, overrides);
  const colours = tokens.filter((t) => isColour(t.value));
  const accents = Array.isArray(profile.tokens.accents)
    ? (profile.tokens.accents as unknown[]).filter(isColour)
    : [];
  const semantic =
    profile.tokens.semantic && typeof profile.tokens.semantic === "object"
      ? Object.entries(profile.tokens.semantic as Record<string, unknown>).filter(([, v]) =>
          isColour(v),
        )
      : [];
  const fonts = tokens.filter((t) => t.key.startsWith("font_"));

  return (
    <div className="space-y-5">
      <div className="space-y-1 text-xs">
        <div className="text-sm text-kw-text-primary">{profile.name}</div>
        <div className="font-mono text-kw-text-faint">{profile.id}</div>
        {profile.description && <p className="text-kw-text-muted">{profile.description}</p>}
        <div className="text-kw-text-muted">
          Declared by <span className="font-mono">{profile.pack_id}</span>{" "}
          <span className="text-kw-text-faint">v{profile.pack_version}</span>
          {profile.extends && (
            <>
              {" · extends "}
              <span className="font-mono">{profile.extends}</span>
            </>
          )}
        </div>
        <div className="flex flex-wrap gap-x-3 text-kw-text-faint">
          {isBound && <span className="text-kw-l1">bound to this realm</span>}
          {packDefaultOf.length > 0 && <span>default of {packDefaultOf.join(", ")}</span>}
          {profile.operator_only && <span className="text-amber-400">operator-only</span>}
        </div>
      </div>

      {colours.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs uppercase tracking-wide text-kw-text-faint">Palette</div>
          <div className="grid grid-cols-2 gap-2">
            {colours.map((t) => (
              <Swatch
                key={t.key}
                label={t.key}
                value={t.value as string}
                overridden={t.overridden}
              />
            ))}
          </div>
        </div>
      )}

      {accents.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs uppercase tracking-wide text-kw-text-faint">
            Accents <span className="normal-case">(order is meaningful)</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {accents.map((c, i) => (
              <Swatch key={`${c}-${i}`} label={`#${i + 1}`} value={c} overridden={false} />
            ))}
          </div>
        </div>
      )}

      {semantic.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs uppercase tracking-wide text-kw-text-faint">Semantic roles</div>
          <div className="grid grid-cols-2 gap-2">
            {semantic.map(([role, c]) => (
              <Swatch key={role} label={role} value={c as string} overridden={false} />
            ))}
          </div>
        </div>
      )}

      {fonts.length > 0 && (
        <div className="space-y-1">
          <div className="text-xs uppercase tracking-wide text-kw-text-faint">Typography</div>
          {fonts.map((t) => (
            <div key={t.key} className="text-xs">
              <span className="text-kw-text-muted">{t.key}</span>{" "}
              <span className="font-mono text-kw-text-faint">{String(t.value)}</span>
              {t.overridden && <span className="text-amber-400"> · realm override</span>}
            </div>
          ))}
        </div>
      )}

      <div className="space-y-2">
        <div className="text-xs uppercase tracking-wide text-kw-text-faint">Anchor images</div>
        <ReferenceImages realmId={realmId} profile={profile} />
      </div>

      {(["visual_guidance", "writing_guidance"] as const).map((key) =>
        profile[key] ? (
          <div key={key} className="space-y-1">
            <div className="text-xs uppercase tracking-wide text-kw-text-faint">
              {key === "visual_guidance" ? "Visual guidance (image prompts)" : "Writing guidance"}
            </div>
            <pre className="text-xs whitespace-pre-wrap text-kw-text-muted bg-kw-bg-raised border border-kw-border-subtle rounded p-3 max-h-72 overflow-y-auto">
              {profile[key]}
            </pre>
          </div>
        ) : null,
      )}
    </div>
  );
}

/**
 * The realm's binding. Moved here from the Models tab so the choice is made where
 * its palette and prompt texts are visible; deliberately the ONLY writer — a
 * second one would be a second semantics.
 */
function BindingSection({
  realmId,
  profiles,
  onChanged,
}: {
  realmId: string;
  profiles: RealmRenderProfile[];
  onChanged: () => void;
}) {
  const grants = useGrants();
  const platformAllowed = canUsePlatformProfiles(grants);
  const selectable = profiles.filter((p) => !p.operator_only || platformAllowed);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const s = useEditableSection<RenderProfileBinding | null, { profileId: string }>({
    load: () => fetchRealmRenderProfile(realmId),
    toDraft: (b) => ({ profileId: b?.profile_id ?? "" }),
    validate: (d) => (d.profileId ? null : "Pick a render profile."),
    submit: async (d) => {
      const b = await setRealmRenderProfile(realmId, d.profileId);
      onChanged();
      return b;
    },
    deps: [realmId],
  });

  async function removeBinding() {
    setDeleteError("");
    setDeleteBusy(true);
    try {
      await deleteRealmRenderProfile(realmId);
      setConfirmDelete(false);
      s.reload();
      onChanged();
    } catch (e: unknown) {
      setDeleteError(describeApiError(e).message);
    } finally {
      setDeleteBusy(false);
    }
  }

  return (
    <SectionCard
      title="Realm style"
      description="Applies to every render in this realm that does not pin a profile itself. Style token overrides stay operator-only (CLI)."
      footer={
        <div className="space-y-3">
          <SaveBar
            dirty={s.dirty}
            busy={s.busy}
            error={s.fieldError || s.error}
            errorKind={s.fieldError ? "validation" : s.errorKind}
            savedAt={s.savedAt}
            onSave={s.save}
            onReset={s.reset}
          />
          {s.loaded && !confirmDelete && (
            <button
              onClick={() => setConfirmDelete(true)}
              className="text-xs text-kw-text-muted hover:text-amber-400"
            >
              Remove binding
            </button>
          )}
          {confirmDelete && (
            <ConfirmPanel
              title="Remove the render profile binding?"
              tone="danger"
              confirmLabel="Remove"
              busy={deleteBusy}
              error={deleteError}
              onConfirm={removeBinding}
              onCancel={() => setConfirmDelete(false)}
            >
              <p>
                Rendering falls back to the default of the pack that owns the rendered
                blueprint, and to the platform default where there is none. Already
                rendered artifacts keep the profile they were rendered with.
              </p>
            </ConfirmPanel>
          )}
        </div>
      }
    >
      {s.loading ? (
        <div className="text-xs text-kw-text-faint">Loading…</div>
      ) : (
        <Field
          label="Profile"
          hint={
            s.loaded
              ? undefined
              : "No binding — rendering falls back to the pack default, then the platform default."
          }
        >
          <Select
            value={s.draft.profileId}
            onChange={(v) => s.patch({ profileId: v })}
            options={selectable.map((p) => ({
              value: p.id,
              label: p.name,
              hint: `${p.pack_id}${p.description ? ` · ${p.description}` : ""}`,
            }))}
            placeholder="— pick a profile —"
          />
        </Field>
      )}
    </SectionCard>
  );
}

export default function RealmRenderProfilesRoute() {
  const { realmId } = useRealmContext();
  const [set, setSet] = useState<RealmRenderProfileSet | null>(null);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!realmId) return;
    let live = true;
    setError("");
    fetchRealmRenderProfiles(realmId)
      .then((s) => {
        if (!live) return;
        setSet(s);
        setSelected((current) => current ?? s.bound_profile_id ?? s.profiles[0]?.id ?? null);
      })
      .catch((e: unknown) => {
        if (live) setError(describeApiError(e).message);
      });
    return () => {
      live = false;
    };
  }, [realmId, reloadKey]);

  if (!realmId) return null;

  const profile = set?.profiles.find((p) => p.id === selected) ?? null;
  const packDefaultsOf = (id: string) =>
    Object.entries(set?.pack_defaults ?? {})
      .filter(([, defaultId]) => defaultId === id)
      .map(([packId]) => packId);

  return (
    <div className="space-y-6">
      {error && (
        <div className="text-xs text-amber-400 border border-amber-500/50 rounded p-3">
          Could not load the realm's render profiles: {error}
        </div>
      )}

      <ScopeGate
        require={(g) => canAdminRealm(g, realmId)}
        requires="realm-admin in this realm"
      >
        <BindingSection
          realmId={realmId}
          profiles={set?.profiles ?? []}
          onChanged={() => setReloadKey((k) => k + 1)}
        />
      </ScopeGate>

      <SectionCard
        title="Available styles"
        description="The union of what this realm's bound packs declare (D-RENDER-15) — and exactly what a render may ask for. Styles are authored in packs, not here."
      >
        {set === null ? (
          <div className="text-xs text-kw-text-faint">Loading…</div>
        ) : set.profiles.length === 0 ? (
          <div className="text-xs text-kw-text-faint">
            No pack bound to this realm declares a render profile.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <ul className="space-y-1 md:col-span-1">
              {set.profiles.map((p) => (
                <li key={p.id}>
                  <button
                    onClick={() => setSelected(p.id)}
                    className={`w-full text-left px-2 py-1.5 rounded text-xs ${
                      p.id === selected
                        ? "bg-kw-bg-raised text-kw-text-primary"
                        : "text-kw-text-muted hover:text-kw-text-primary"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {/* The palette is the fastest way to recognize a style, so
                          the list carries it rather than only the name. */}
                      <span className="flex shrink-0">
                        {(Array.isArray(p.tokens.accents) ? p.tokens.accents : [])
                          .filter(isColour)
                          .slice(0, 4)
                          .map((c, i) => (
                            <span
                              key={`${c}-${i}`}
                              className="inline-block w-2.5 h-4 border border-kw-border-subtle"
                              style={{ backgroundColor: c }}
                            />
                          ))}
                      </span>
                      <span className="truncate">{p.name}</span>
                      {p.id === set.bound_profile_id && <span className="text-kw-l1">●</span>}
                    </div>
                    <div className="font-mono text-kw-text-faint truncate">{p.id}</div>
                  </button>
                </li>
              ))}
            </ul>
            <div className="md:col-span-2">
              {profile && (
                <ProfileDetail
                  realmId={realmId}
                  profile={profile}
                  overrides={set.style_token_overrides}
                  isBound={profile.id === set.bound_profile_id}
                  packDefaultOf={packDefaultsOf(profile.id)}
                />
              )}
            </div>
          </div>
        )}
      </SectionCard>
    </div>
  );
}
